// ─── PILIHAN VOUCHER KERANJANG (mig 5660) — disimpan di localStorage ────────
//
// Halaman /keranjang/voucher menulis pilihan, /keranjang membacanya saat mount
// lalu memvalidasi ke server (`preview_my_voucher`) — voucher yang gugur dilepas
// otomatis dan kuncinya dibersihkan. Sama seperti keranjang itu sendiri
// (`galea-preorder-cart`), ia hidup per origin = per toko.
//
// Pilihan MILIK SATU PEMBELI: `customerId` ikut disimpan supaya akun lain yang
// login di HP yang sama tidak mewarisi — dan tidak ikut memvalidasi — voucher
// akun sebelumnya. Entri tanpa `customerId` = bentuk lama (sebelum audit
// 2026-09-10) → data tak dikenal, dibuang tanpa crash. Kuncinya juga dibersihkan
// setelah checkout ber-voucher sukses dan saat logout, bukan dibiarkan menempel.
//
// VOUCHER BER-KODE (mig 6990): kode yang diketik pembeli ikut disimpan di sini
// sampai checkout itu selesai — keputusan pemilik "kode berlaku untuk checkout
// SAAT ITU": server tak menyimpannya ke akun, jadi pratinjau & checkout harus
// membawanya. Kunci ini dibersihkan sesudah checkout, jadi checkout berikutnya
// wajib mengetik ulang.

import { normalizeVoucherCode } from "@/lib/voucher";

export const CART_VOUCHER_KEY = "galea-cart-voucher";

export type CartVoucherPick = {
  voucherId: string;
  voucherName: string;
  /** Pembeli yang memilih (customers.id) — pilihan akun lain diabaikan. */
  customerId: string;
  pickedAt: number;
  /** Kode yang diketik untuk voucher ber-kode (ternormalisasi); null = voucher daftar. */
  code: string | null;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Pemeriksa bentuk — nilai localStorage bisa dari versi lama / diedit tangan. */
export function parseCartVoucherPick(raw: unknown): CartVoucherPick | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const voucherId = typeof r.voucherId === "string" ? r.voucherId.trim() : "";
  if (!UUID_RE.test(voucherId)) return null;
  // Tanpa pemilik tak bisa dipastikan pilihan siapa → jangan dipakai.
  const customerId = typeof r.customerId === "string" ? r.customerId.trim() : "";
  if (!customerId) return null;
  const voucherName = typeof r.voucherName === "string" && r.voucherName.trim() ? r.voucherName.trim() : "Voucher";
  const pickedAt = typeof r.pickedAt === "number" && Number.isFinite(r.pickedAt) ? r.pickedAt : 0;
  const code = typeof r.code === "string" ? normalizeVoucherCode(r.code) : null;
  return { voucherId, voucherName, customerId, pickedAt, code };
}

/**
 * Pilihan yang SAH untuk pembeli ini: milik akun lain (atau tanpa pembeli yang
 * sedang login) diabaikan — bukan dihapus, karena akun pemiliknya bisa kembali.
 */
export function pickForCustomer(
  pick: CartVoucherPick | null,
  customerId: string | null | undefined
): CartVoucherPick | null {
  if (!pick) return null;
  const id = typeof customerId === "string" ? customerId.trim() : "";
  if (!id || pick.customerId !== id) return null;
  return pick;
}

export function readCartVoucherPick(): CartVoucherPick | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(CART_VOUCHER_KEY);
    return raw ? parseCartVoucherPick(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/** Pembaca untuk UI: pilihan pembeli yang sedang login saja. */
export function readCartVoucherPickFor(customerId: string | null | undefined): CartVoucherPick | null {
  return pickForCustomer(readCartVoucherPick(), customerId);
}

export function writeCartVoucherPick(
  pick: Pick<CartVoucherPick, "voucherId" | "voucherName" | "customerId"> & { code?: string | null }
): void {
  try {
    if (typeof window === "undefined") return;
    const value: CartVoucherPick = {
      voucherId: pick.voucherId,
      voucherName: pick.voucherName,
      customerId: pick.customerId,
      pickedAt: Date.now(),
      code: normalizeVoucherCode(pick.code)
    };
    window.localStorage.setItem(CART_VOUCHER_KEY, JSON.stringify(value));
  } catch {
    /* localStorage bisa ditolak (mode privat) — pilihan cukup hidup di state halaman */
  }
}

export function clearCartVoucherPick(): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(CART_VOUCHER_KEY);
  } catch {
    /* idem */
  }
}
